import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2, Lock } from "lucide-react";
import {
  HANDOFF_REASON_CATEGORIES,
  type HandoffReasonCategory,
  getHandoffReason,
} from "@shared/v2/handoffReasons";

export interface HandoffReasonResult {
  reasonCategory: HandoffReasonCategory;
  reasonNote?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentHolder: string;
  newHolder: string;
  itemTitle: string;
  isSubmitting?: boolean;
  /** When false (default), the "Insufficient progress" radio is disabled
   *  and the "Escalate as dropped-the-ball" option is shown instead. */
  canFlagInsufficientProgress?: boolean;
  /** Deprecated — escalations now auto-route to the holder's C-suite via the
   *  org chart, so no manual reviewer is picked here. Accepted for backward
   *  compatibility with existing call sites but no longer used. */
  cSuitePeople?: string[];
  onConfirm: (result: HandoffReasonResult) => void;
}

export function HandoffReasonModal({
  open,
  onOpenChange,
  currentHolder,
  newHolder,
  itemTitle,
  isSubmitting,
  canFlagInsufficientProgress = false,
  onConfirm,
}: Props) {
  const [category, setCategory] = useState<HandoffReasonCategory | "">("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) {
      setCategory("");
      setNote("");
    }
  }, [open]);

  const cat = category ? getHandoffReason(category) : null;
  const noteRequired = !!cat?.requiresNote;
  const noteValid = !noteRequired || note.trim().length > 0;
  const isEscalation = category === "ESCALATED_PENDING_REVIEW";
  const canConfirm = !!category && noteValid && !isSubmitting;

  // Filter visible reasons based on caller capability:
  //   - If caller can flag INSUFFICIENT_PROGRESS directly, hide the
  //     ESCALATED_PENDING_REVIEW option (no need to escalate).
  //   - Otherwise, show every option but render INSUFFICIENT_PROGRESS as
  //     disabled with an inline note pointing to the escalate row.
  const visibleReasons = HANDOFF_REASON_CATEGORIES.filter((c) =>
    canFlagInsufficientProgress ? c.key !== "ESCALATED_PENDING_REVIEW" : true,
  );

  function handleConfirm() {
    if (!canConfirm || !category) return;
    onConfirm({
      reasonCategory: category as HandoffReasonCategory,
      reasonNote: note.trim() ? note.trim() : undefined,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg bg-[#0f1d40] border-white/10 text-white max-h-[90vh] overflow-y-auto"
        data-testid="dialog-handoff-reason"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-[#FF9F43]" />
            Reassign from {currentHolder}?
          </DialogTitle>
          <DialogDescription className="text-white/60">
            This item is currently with <span className="text-white">{currentHolder}</span>.
            You are moving it to <span className="text-white">{newHolder}</span>.
            Please pick a reason for the audit record.
          </DialogDescription>
        </DialogHeader>

        <div className="text-xs text-white/40 truncate" title={itemTitle}>
          {itemTitle}
        </div>

        <div className="py-2">
          <RadioGroup
            value={category}
            onValueChange={(v) => setCategory(v as HandoffReasonCategory)}
            className="space-y-2"
          >
            {visibleReasons.map((c) => {
              const isInsufficient = c.key === "INSUFFICIENT_PROGRESS";
              const isEscalateRow = c.key === "ESCALATED_PENDING_REVIEW";
              const disabled = isInsufficient && !canFlagInsufficientProgress;
              return (
                <Label
                  key={c.key}
                  htmlFor={`reason-${c.key}`}
                  className={`flex items-start gap-3 p-3 rounded-md border ${
                    disabled
                      ? "border-white/5 bg-white/[0.02] cursor-not-allowed opacity-60"
                      : isEscalateRow
                      ? "border-[#FF9F43]/30 bg-[#FF9F43]/5 hover:bg-[#FF9F43]/10 cursor-pointer"
                      : "border-white/10 hover:bg-white/5 cursor-pointer"
                  }`}
                  data-testid={`label-reason-${c.key}`}
                >
                  <RadioGroupItem
                    value={c.key}
                    id={`reason-${c.key}`}
                    data-testid={`radio-reason-${c.key}`}
                    disabled={disabled}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="text-sm text-white flex items-center gap-1.5">
                      {disabled && <Lock className="h-3 w-3 text-white/40" />}
                      {c.label}
                      {c.requiresNote && !disabled && (
                        <span className="ml-1 text-xs text-[#FF9F43]">note required</span>
                      )}
                    </div>
                    {c.sublabel && (
                      <div className="text-xs text-white/40 mt-0.5">{c.sublabel}</div>
                    )}
                    {disabled && (
                      <div className="text-sm text-white/50 mt-1 italic">
                        Restricted — escalate via the option below to have a senior reviewer confirm or dismiss the KPI flag.
                      </div>
                    )}
                  </div>
                </Label>
              );
            })}
          </RadioGroup>
        </div>

        {isEscalation && (
          <div className="space-y-1.5 -mt-1">
            <div className="text-sm text-white/80 bg-[#FF9F43]/5 border border-[#FF9F43]/20 rounded p-3" data-testid="text-escalation-autoroute">
              This will be routed automatically to <strong>{currentHolder}</strong>'s senior reviewer (their C-suite) — you don't pick who.
              The item moves into that reviewer's queue and keeps counting days past due until they act.
            </div>
            <p className="text-xs text-white/40">
              The senior reviewer decides whether this was a dropped-the-ball (a KPI hit on {currentHolder}) or not,
              and may keep it or reassign it within their own team. You'll be emailed the outcome.
              The displaced holder is <strong>not</strong> told about the escalation by email — that conversation is yours to have in person.
            </p>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="reason-note" className="text-xs text-white/60">
            Note {noteRequired ? <span className="text-[#FF9F43]">(required)</span> : <span className="text-white/30">(optional)</span>}
          </Label>
          <Textarea
            id="reason-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={
              isEscalation
                ? "Briefly describe what happened — the senior reviewer will see this."
                : noteRequired
                ? "Briefly describe the reason for this reassignment."
                : "Optional context for the new holder."
            }
            className="bg-white/5 border-white/10 text-white text-sm min-h-[80px]"
            data-testid="textarea-reason-note"
          />
          <p className="text-xs text-white/30">
            Free-text notes shown above are visible to anyone with access to the item.
            The reason category is recorded for governance review.
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            data-testid="button-cancel-reason"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="bg-[#FF9F43] hover:bg-[#FF9F43]/90 text-black"
            data-testid="button-confirm-reason"
          >
            {isSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            {isEscalation ? "Reassign & escalate" : "Confirm reassignment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
